import React from 'react'
import {observer} from 'mobx-react-lite'
import {
    Button,
    ButtonGroup,
    ButtonProps,
    ClickAwayListener,
    Grow,
    Paper,
    Popper,
    MenuItem,
    MenuList,
    Tooltip
} from '@mui/material'
import {ArrowDropDown} from '@mui/icons-material'

export interface SplitButtonDefinition {
    title: string
    key: string
    tooltip?: string[]
    props?: ButtonProps
}

export interface SplitButtonProps extends ButtonProps {
    options: SplitButtonDefinition[]
    defaultSelectedKey?: string
    onSelected?: (key: string, index: number) => void
}

function _SplitButton({options, onSelected, defaultSelectedKey, ...otherProps}: SplitButtonProps) {
    const [open, setOpen] = React.useState(false)

    const defaultSelectedOptionIndex = React.useMemo(
        () => options.findIndex(o => o.key === defaultSelectedKey),
        [options, defaultSelectedKey]
    )

    const [selectedIndex, setSelectedIndex] = React.useState(
        defaultSelectedOptionIndex >= 0 ? defaultSelectedOptionIndex : 0
    )
    const anchorRef = React.useRef<HTMLDivElement>(null)

    const handleMenuItemClick = React.useCallback(
        (index: number) => {
            setSelectedIndex(index)
            if (onSelected) onSelected(options[index].key, index)
            setOpen(false)
        },
        [onSelected, options]
    )

    const toggleMenuOpen = React.useCallback(() => setOpen(prevOpen => !prevOpen), [])

    const handleClose = React.useCallback((event: MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return
        }

        setOpen(false)
    }, [])

    React.useEffect(() => {
        if (defaultSelectedOptionIndex === -1) {
            setSelectedIndex(0)
        } else {
            setSelectedIndex(defaultSelectedOptionIndex)
        }
    }, [defaultSelectedOptionIndex])

    const selectedOptionTooltip = React.useMemo(() => {
        return options[selectedIndex]?.tooltip
    }, [options, selectedIndex])

    return (
        <div>
            <ButtonGroup variant="contained" color="primary" ref={anchorRef} aria-label="split button">
                {selectedOptionTooltip ? (
                    <Tooltip
                        title={selectedOptionTooltip.map((text, textIndex) => (
                            <div key={`split-tooltip-selected-text${textIndex}`}>{text}</div>
                        ))}
                        key={`split-selected-tooltip-${selectedIndex}`}
                    >
                        <Button {...otherProps} {...options[selectedIndex]?.props}>
                            {options[selectedIndex]?.title}
                        </Button>
                    </Tooltip>
                ) : (
                    <Button {...otherProps} {...options[selectedIndex]?.props}>
                        {options[selectedIndex]?.title}
                    </Button>
                )}
                <Button
                    color="primary"
                    size="small"
                    aria-controls={open ? 'split-button-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-haspopup="menu"
                    onClick={toggleMenuOpen}
                    disabled={otherProps.disabled || options[selectedIndex]?.props?.disabled || false}
                >
                    <ArrowDropDown />
                </Button>
            </ButtonGroup>
            <Popper open={open} anchorEl={anchorRef.current} role={undefined} transition>
                {({TransitionProps, placement}) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom'
                        }}
                    >
                        <Paper square>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList id="split-button-menu">
                                    {options.map((option, index) => (
                                        <MenuItem
                                            key={`split-button-${index}`}
                                            disabled={otherProps.disabled || option.props?.disabled || false}
                                            selected={index === selectedIndex}
                                            onClick={() => handleMenuItemClick(index)}
                                        >
                                            {option.tooltip ? (
                                                <Tooltip
                                                    title={option.tooltip.map((text, textIndex) => (
                                                        <div key={`split-tooltip-${index}-text${textIndex}`}>
                                                            {text}
                                                        </div>
                                                    ))}
                                                    key={`split-tooltip-${index}`}
                                                >
                                                    <span>{option.title}</span>
                                                </Tooltip>
                                            ) : (
                                                <span>{option.title}</span>
                                            )}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </div>
    )
}
export const SplitButton = observer(_SplitButton)

export default SplitButton
